import { PlaneLanding, Building2, Clock, LogIn, LogOut, Eye, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { TransientStayTableRow } from '@/types/transient-stays'

export type TransientCardVariant = 'arriving' | 'on_ground' | 'departing' | 'upcoming'

interface TransientCardProps {
  transient: TransientStayTableRow
  variant: TransientCardVariant
  onCheckIn?: (id: string) => void
  onCheckOut?: (id: string) => void
  onView?: (id: string) => void
}

const EXTENDED_STAY_THRESHOLD = 5

/**
 * Compact card for displaying transient stay in Today/Upcoming views
 * Displays: Tail # | Aircraft Type | Client | Time | Unit | Flags | Actions
 */
export function TransientCard({
  transient,
  variant,
  onCheckIn,
  onCheckOut,
  onView,
}: TransientCardProps) {
  const showCheckIn = variant === 'arriving' && onCheckIn
  const showCheckOut = (variant === 'on_ground' || variant === 'departing') && onCheckOut

  // Anomaly flag conditions
  const isNotAssigned = !transient.unit_id && (variant === 'arriving' || variant === 'upcoming')
  const isCheckoutToday = variant === 'departing'
  const nightNumber = variant === 'on_ground' ? getNightNumber(transient.check_in) : null
  const isExtendedStay = variant === 'on_ground' && nightNumber !== null && nightNumber >= EXTENDED_STAY_THRESHOLD
  const isOpenEnded = variant === 'on_ground' && !transient.expected_check_out

  return (
    <div className="rounded-lg border bg-card hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between p-3">
        {/* Left: Aircraft & Client Info */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Tail Number */}
          <div className="flex items-center gap-2 min-w-0">
            <PlaneLanding className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="font-mono font-bold text-foreground truncate">
              {transient.tail_number || '???'}
            </span>
          </div>

          {/* Aircraft Type */}
          <div className="hidden sm:block text-sm text-muted-foreground truncate max-w-[120px]">
            {transient.aircraft_type_name || 'Unknown'}
          </div>

          {/* Client */}
          <div className="text-sm font-medium text-foreground truncate max-w-[150px]">
            {transient.client_name || 'Walk-in'}
          </div>
        </div>

        {/* Center: Time Info */}
        <div className="flex items-center gap-4 px-4">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap">
              {variant === 'arriving' && `ETA ${formatTime(transient.check_in)}`}
              {variant === 'on_ground' && (
                <>
                  Since {formatTime(transient.check_in)}
                  {transient.expected_check_out && (
                    <span className="text-muted-foreground ml-1.5">· ETD {formatTime(transient.expected_check_out)}</span>
                  )}
                </>
              )}
              {variant === 'departing' && `ETD ${formatTime(transient.expected_check_out)}`}
              {variant === 'upcoming' && `ETA ${formatTime(transient.check_in)}`}
            </span>
          </div>

          {/* Nights indicator for on_ground */}
          {variant === 'on_ground' && transient.nights_count && nightNumber && (
            <span className="text-xs text-muted-foreground">
              Day {nightNumber} of {transient.nights_count}
            </span>
          )}
        </div>

        {/* Right: Unit & Actions */}
        <div className="flex items-center gap-3">
          {/* Unit/Space */}
          <div className="flex items-center gap-1.5 text-sm">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className={transient.unit_code ? 'font-medium' : 'text-orange-600 dark:text-orange-500 font-medium'}>
              {transient.unit_code || 'Not assigned'}
            </span>
          </div>

          {/* Inline status badges */}
          {isCheckoutToday && (
            <Badge variant="secondary" className="text-xs">
              {transient.status === 'checked_in' ? 'Awaiting checkout' : 'Checkout today'}
            </Badge>
          )}

          {/* Actions */}
          <div className="flex items-center gap-1">
            {showCheckIn && (
              <Button
                size="sm"
                variant="default"
                onClick={() => onCheckIn(transient.id)}
                className="h-7 gap-1.5"
              >
                <LogIn className="h-3.5 w-3.5" />
                Check In
              </Button>
            )}

            {showCheckOut && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCheckOut(transient.id)}
                className="h-7 gap-1.5"
              >
                <LogOut className="h-3.5 w-3.5" />
                Check Out
              </Button>
            )}

            {onView && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onView(transient.id)}
                className="h-7 w-7 p-0"
              >
                <Eye className="h-3.5 w-3.5" />
                <span className="sr-only">View</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Anomaly flags row (below main row when flags are present) */}
      {(isNotAssigned || isOpenEnded || isExtendedStay) && (
        <div className="flex items-center gap-3 border-t px-3 py-1.5 text-xs">
          {isNotAssigned && (
            <span className="flex items-center gap-1 text-orange-600 dark:text-orange-500">
              <AlertTriangle className="h-3 w-3" />
              Not assigned — needs space allocation
            </span>
          )}
          {isOpenEnded && (
            <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-500">
              <AlertTriangle className="h-3 w-3" />
              Open-ended — no departure date
            </span>
          )}
          {isExtendedStay && !isOpenEnded && (
            <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-500">
              <AlertTriangle className="h-3 w-3" />
              Extended stay ({nightNumber} nights)
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Format time for display (e.g., "2:30 PM")
 */
function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Calculate which night the guest is on
 */
function getNightNumber(checkIn: string): number {
  const start = new Date(checkIn)
  const now = new Date()
  const diffTime = now.getTime() - start.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays + 1)
}
